import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Search } from "lucide-react";
import { getChordLabel } from "@/music/chords/chordSyncUtils";

const COMMON_CHORDS = [
  "C", "Cm", "C7", "Cmaj7", "D", "Dm", "D7", "E", "Em", "E7",
  "F", "Fm", "Fmaj7", "G", "Gm", "G7", "A", "Am", "A7", "Bb", "B", "Bm",
];
const KEY_SUGGESTIONS = {
  C: ["C", "Dm", "Em", "F", "G", "Am"],
  G: ["G", "Am", "Bm", "C", "D", "Em"],
  D: ["D", "Em", "F#m", "G", "A", "Bm"],
  A: ["A", "Bm", "C#m", "D", "E", "F#m"],
  F: ["F", "Gm", "Am", "Bb", "C", "Dm"],
  Bb: ["Bb", "Cm", "Dm", "Eb", "F", "Gm"],
};
const CHORD_PATTERN = /^(--|[A-G](?:#|b)?[A-Za-z0-9+#/()\-]*)$/;

const AdminChordSelect = ({
  value,
  onChange,
  songKey = "",
  recentChords = [],
  previousChord = null,
  onUsePrevious,
  focusToken = 0,
}) => {
  const selectedLabel = getChordLabel(value);
  const [draft, setDraft] = useState(selectedLabel);
  const inputRef = useRef(null);
  const keyRoot = String(songKey || "").replace(/m(in(or)?)?$/i, "");
  const keySuggestions = KEY_SUGGESTIONS[keyRoot] || COMMON_CHORDS.slice(0, 6);
  const valid = CHORD_PATTERN.test(draft.trim());

  useEffect(() => { setDraft(selectedLabel); }, [selectedLabel]);
  useEffect(() => {
    if (focusToken > 0) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [focusToken]);

  const suggestions = useMemo(() => {
    const search = draft.trim().toLowerCase();
    const source = search && search !== selectedLabel.toLowerCase()
      ? COMMON_CHORDS.filter((chord) => chord.toLowerCase().includes(search))
      : keySuggestions;
    return [...new Set(source)].slice(0, 10);
  }, [draft, keySuggestions, selectedLabel]);
  const recents = [...new Set(recentChords.map((item) => getChordLabel(item)).filter((item) => item !== "--"))].slice(0, 5);

  const applyDraft = () => {
    if (valid) onChange(draft.trim());
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase text-slate-400">Acorde</p>
      <label className={`flex min-h-11 items-center gap-2 rounded-xl border bg-black/20 px-3 ${valid ? "border-cyan-300/35" : "border-red-300/45"}`}>
        <Search className="h-4 w-4 text-slate-500" />
        <input
          ref={inputRef}
          className="w-full bg-transparent text-sm text-white outline-none"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.stopPropagation();
              applyDraft();
            }
          }}
          placeholder="Cm, G7, Bbmaj7, F#m"
        />
        <button type="button" disabled={!valid} onClick={applyDraft} aria-label="Aplicar acorde" className="grid h-8 w-8 place-items-center rounded-lg bg-cyan-300/15 text-cyan-100 disabled:opacity-30">
          <Check className="h-4 w-4" />
        </button>
      </label>
      {!valid && <p className="text-xs text-red-200">Escribe un acorde valido, por ejemplo Cm, G7 o Bbmaj7.</p>}
      <div>
        <p className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-500">Sugeridos por key</p>
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((chord) => (
            <button key={chord} type="button" onClick={() => onChange(chord)} className={`min-h-9 rounded-lg border px-3 text-sm font-semibold ${chord === selectedLabel ? "border-cyan-300 bg-cyan-300 text-slate-950" : "border-white/10 bg-white/5 text-slate-200 hover:border-cyan-300/40"}`}>
              {chord}
            </button>
          ))}
        </div>
      </div>
      {recents.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-500">Recientes</p>
          <div className="flex flex-wrap gap-1.5">
            {recents.map((chord) => <button key={chord} type="button" onClick={() => onChange(chord)} className="rounded-lg border border-violet-300/20 px-2.5 py-1.5 text-xs text-violet-100">{chord}</button>)}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onUsePrevious} disabled={!previousChord} className="min-h-9 rounded-lg border border-white/10 px-3 text-xs font-semibold text-slate-200 disabled:opacity-35">
          Usar anterior {previousChord ? `(${getChordLabel(previousChord)})` : ""}
        </button>
        <button type="button" onClick={() => onChange("--")} className="min-h-9 rounded-lg border border-white/10 px-3 text-xs font-semibold text-slate-200">
          Sin acorde
        </button>
      </div>
    </div>
  );
};

export default AdminChordSelect;
